import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ContractTemplate } from '../../../hooks/useContractTemplate';
import Card from '../../../components/Card/Card';
import ListContainer from '../../../components/ListContainer/ListContainer';
import ListElement from '../../../components/ListElement/ListElement';
import ElementActions from '../../../components/ElementActions/ElementActions';
import Subtitle from '../../../components/Subtitle/Subtitle';
import TinyText from '../../../components/TinyText/TinyText';
import SecondaryButton from '../../../components/SecondaryButton/SecondaryButton';
import PrimaryButton from '../../../components/PrimaryButton/PrimaryButton';
import TextArea from '../../../components/TextArea/TextArea';
import IconButton from '../../../components/IconButton/IconButton';
import { Icon } from '../../../components/Icon/Icon';
import deleteIcon from '../../../assets/images/bin.svg';
import { isValidText } from '../../../shared/validation';

const DEFAULT_CONTRACT_CONDITION_KEYS = [
  'contracts.conditions.point1',
  'contracts.conditions.point2',
  'contracts.conditions.point2_2',
  'contracts.conditions.point3',
  'contracts.conditions.point4',
  'contracts.conditions.point5',
  'contracts.conditions.point6',
  'contracts.conditions.point7',
];

type ConditionItem = { id: string; text: string };

type Props = {
  contractTemplate: ContractTemplate;
  updateContractTemplate: (value: any) => void;
};

export default function OrderConditionsSection({ contractTemplate, updateContractTemplate }: Props) {
  const { t } = useTranslation();
  const currentConditions = contractTemplate?.conditions?.conditions || [];
  const [newItem, setNewItem] = useState('');

  const elements: ConditionItem[] = currentConditions.map((text, i) => ({
    id: `condition-${i}`,
    text,
  }));

  const handleAddDefaultConditions = () => {
    const defaultTexts = DEFAULT_CONTRACT_CONDITION_KEYS.map((key) => t(key)).filter(Boolean);
    updateContractTemplate({ conditions: { conditions: [...currentConditions, ...defaultTexts] } });
  };

  const handleMovedItem = (reordered: ConditionItem[]) => {
    updateContractTemplate({ conditions: { conditions: reordered.map((item) => item.text) } });
  };

  const handleDeleteItem = (index: number) => {
    const updated = currentConditions.filter((_, i) => i !== index);
    updateContractTemplate({ conditions: { conditions: updated } });
  };

  const handleAddItem = () => {
    if (newItem.trim()) {
      updateContractTemplate({ conditions: { conditions: [...currentConditions, newItem.trim()] } });
      setNewItem('');
    }
  };

  return (
    <Card>
      <>
        <Subtitle text={t('contract_template.order.title')} />
        <TinyText text={t('contract_template.order.hint')} />
        <div className="mt-2 mb-3">
          <SecondaryButton
            text={t('contract_template.order.add_default_conditions') ?? 'Add default conditions'}
            fn={handleAddDefaultConditions}
          />
        </div>
        <ListContainer
          elements={elements}
          enableDragAndDrop
          onMovedItem={handleMovedItem}
          toElementFn={(item: ConditionItem, index: number) => (
            <ListElement>
              <ElementActions
                actions={
                  <IconButton onClick={() => handleDeleteItem(index)}>
                    <Icon url={deleteIcon} cssStyles="w-5" />
                  </IconButton>
                }
              >
                <div className="flex-1 min-w-0">{item.text}</div>
              </ElementActions>
            </ListElement>
          )}
        />
        <TextArea
          label={String(t('contract_template.order.form.new_condition.label') ?? '')}
          validationFn={(value) => !!value && isValidText(value, 5)}
          defaultValue={newItem}
          onChange={(e) => setNewItem(e.target.value)}
        />
        <div className="container flex flex-wrap space-x-5 mt-2">
          <PrimaryButton
            text={String(t('contract_template.order.form.button') ?? '')}
            fn={handleAddItem}
          />
        </div>
      </>
    </Card>
  );
}
